import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle, Phone, Mail } from "lucide-react";
import {
  siteInfo, heroContent, jbtBadge, brandSection,
  eleganceSections, nySection, customizationSection,
  ethicalSection, categories } from
"../data/mockData";

const FadeInSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}>

      {children}
    </motion.div>);

};

/* Full-width parallax image section with scroll-triggered text */
const ParallaxImageSection = ({ section, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const textOpacity = useTransform(scrollYProgress, [0.15, 0.35, 0.65, 0.85], [0, 1, 1, 0]);
  const textY = useTransform(scrollYProgress, [0.15, 0.35, 0.65, 0.85], [60, 0, 0, -60]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);

  return (
    <section ref={ref} className="relative h-[100vh] overflow-hidden">
      <motion.div
        className="absolute inset-0"
        style={{ scale: imgScale }}>

        <div
          className="w-full h-full bg-cover bg-center md:bg-top"
          style={{ backgroundImage: `url(${section.image})` }}
          onContextMenu={(e) => e.preventDefault()}
          title={section.heading}
        />

        <div className="absolute inset-0 bg-black/20" />
      </motion.div>
      <motion.div
        className={`absolute inset-0 flex items-center z-10 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}
        style={{ opacity: textOpacity, y: textY }}>

        <div className={`max-w-xl px-10 lg:px-20 ${index % 2 === 0 ? '' : 'text-right'}`}>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight !mr-[0px] text-white"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>

            {section.heading}
            <br />
            <span className="text-[#c8a96e]">{section.subheading}</span>
          </h2>
          <p className="text-[15px] leading-[1.8] !mt-[24px] text-white/70"
          style={{ fontFamily: "'Inter', sans-serif" }}>
            {section.paragraph}
          </p>
          <motion.div className="h-[1px] w-16 mt-6 ml-auto bg-[#c8a96e]" />
        </div>
      </motion.div>
    </section>);

};

/* Customization parallax section (extracted as proper component for hooks) */
const CustomizationParallax = () => {
  const customRef = useRef(null);
  const { scrollYProgress: customScroll } = useScroll({
    target: customRef,
    offset: ["start end", "end start"]
  });
  const customOpacity = useTransform(customScroll, [0.15, 0.3, 0.7, 0.85], [0, 1, 1, 0]);
  const customY = useTransform(customScroll, [0.15, 0.3, 0.7, 0.85], [50, 0, 0, -50]);

  return (
    <section ref={customRef} className="relative h-[100vh] overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="w-full h-[100vh] bg-cover bg-center md:bg-top"
          style={{ backgroundImage: `url(${customizationSection.image})` }}
          onContextMenu={(e) => e.preventDefault()}
          title="Customization"
        />

        <div className="absolute inset-0 bg-black/20" />
      </div>
      <motion.div
        className="absolute inset-0 flex items-center z-10"
        style={{ opacity: customOpacity, y: customY }}>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            <h3 className="text-[12px] tracking-[0.3em] uppercase text-[#c8a96e] mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}>
              {customizationSection.label}
            </h3>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>

              {customizationSection.heading}
            </h2>
            <p className="mt-8 text-white/65 text-[15px] leading-[1.8]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
              {customizationSection.paragraph}
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-2 mt-10 px-8 py-3 border border-[#c8a96e] text-[#c8a96e] text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
              style={{ fontFamily: "'Inter', sans-serif" }}>

              {customizationSection.ctaText}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.div>
    </section>);

};

/* ── Explore by Category Carousel ── */
const CategoryCarousel = () => {
  const cats = categories.slice(0, 10);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent(prev => (prev + 1) % cats.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [paused, cats.length]);

  const goTo = (index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  const goPrev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + cats.length) % cats.length);
  };
  const goNext = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % cats.length);
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir < 0 ? '100%' : '-100%', opacity: 0 }),
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{ height: 'min(80vh, 720px)' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ── Slides ── */}
      <AnimatePresence custom={direction} initial={false}>
        <motion.div
          key={current}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="absolute inset-0"
        >
          <img
            src={cats[current].image}
            alt={cats[current].name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />

          {/* Category label */}
          <motion.div
            className="absolute inset-0 flex flex-col items-center justify-end pb-28"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.55, ease: 'easeOut' }}
          >
            <p
              className="text-[#c8a96e] text-[10px] tracking-[0.45em] uppercase mb-3"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Explore Category
            </p>
            <h3
              className="text-white text-5xl md:text-6xl lg:text-7xl font-light text-center px-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {cats[current].name}
            </h3>
            <div className="h-[1px] w-14 bg-[#c8a96e] mt-5" />
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* ── Progress bar ── */}
      {!paused && (
        <motion.div
          key={`progress-${current}`}
          className="absolute top-0 left-0 h-[2px] bg-[#c8a96e] z-20"
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: 4.5, ease: 'linear' }}
        />
      )}

      {/* ── Prev / Next arrows ── */}
      <button
        onClick={goPrev}
        aria-label="Previous category"
        className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center border border-white/30 text-white text-2xl hover:border-[#c8a96e] hover:text-[#c8a96e] transition-all duration-300"
      >
        &#8249;
      </button>
      <button
        onClick={goNext}
        aria-label="Next category"
        className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center border border-white/30 text-white text-2xl hover:border-[#c8a96e] hover:text-[#c8a96e] transition-all duration-300"
      >
        &#8250;
      </button>

      {/* ── Dot / dash indicators ── */}
      <div className="absolute bottom-10 left-0 right-0 flex justify-center items-center gap-2 z-20">
        {cats.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to ${cats[i].name}`}
            className={`h-[2px] rounded-full transition-all duration-500 ${
              i === current ? 'w-8 bg-[#c8a96e]' : 'w-4 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const HomePage = () => {
  return (
    <div className="overflow-hidden">
      {/* ====== HERO SECTION ====== */}
      <section className="relative h-screen flex items-center justify-center bg-[#2a2a2a]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroContent.backgroundImage})` }} />

        <div className="absolute inset-0 bg-black/60" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.3 }}
          className="relative z-10 text-center px-6">

          <h1
            className="text-4xl md:text-6xl lg:text-7xl text-white font-light leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>

            {heroContent.headline}
            <br />
            <span className="text-[#c8a96e]">{heroContent.brandName}</span>
          </h1>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="h-[1px] bg-[#c8a96e] mx-auto mt-8" />

        </motion.div>

        {/* JBT Badge Only */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="absolute bottom-10 left-0 right-0 z-10">

          <div className="flex justify-center items-center gap-4 px-6">
            <img
              src={jbtBadge.logo}
              alt={jbtBadge.name}
              className="h-10 w-auto object-contain opacity-80" />

            <span className="text-white/50 text-[11px] tracking-[0.15em] uppercase"
            style={{ fontFamily: "'Inter', sans-serif" }}>
              {jbtBadge.name}
            </span>
          </div>
        </motion.div>
      </section>

      {/* ====== WHERE QUALITY MEETS VISIONARY GROWTH ====== */}
      <section className="py-24 lg:py-32 bg-[#faf9f7]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeInSection>
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl text-[#1a1a1a] font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>

                {brandSection.heading}
              </h2>
              <p className="mt-6 max-w-3xl mx-auto text-[#555] text-[15px] leading-[1.8]"
              style={{ fontFamily: "'Inter', sans-serif" }}>
                {brandSection.paragraph}
              </p>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
            {brandSection.blocks.map((block, i) =>
            <FadeInSection key={block.title} delay={i * 0.15}>
                <div className="text-center p-10 bg-white hover:shadow-lg transition-shadow duration-500">
                  <h3
                  className="text-2xl text-[#1a1a1a] mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>

                    {block.title}
                  </h3>
                  <div className="h-[1px] w-12 bg-[#c8a96e] mx-auto mb-6" />
                  <p className="text-[#666] text-[14px] leading-[1.8]"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                    {block.description}
                  </p>
                </div>
              </FadeInSection>
            )}
          </div>
        </div>
      </section>

      {/* ====== FULL-WIDTH PARALLAX ELEGANCE SECTIONS ====== */}
      {eleganceSections.map((section, i) =>
      <ParallaxImageSection key={i} section={section} index={i} />
      )}

      {/* ====== DESIGNED IN NYC ====== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInSection>
              <div className="relative">
                <img
                  src={nySection.image}
                  alt="New York City"
                  className="w-full h-[500px] object-cover"
                  loading="lazy" />

                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#c8a96e]" />
              </div>
            </FadeInSection>
            <FadeInSection delay={0.2}>
              <div>
                <h2
                  className="text-3xl md:text-4xl lg:text-[44px] text-[#1a1a1a] font-light leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>

                  {nySection.heading}
                </h2>
                <p className="mt-8 text-[#555] text-[15px] leading-[1.8] max-w-lg"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                  {nySection.paragraph}
                </p>
                <Link
                  to="/about-us"
                  className="inline-flex items-center gap-2 mt-8 text-[12px] tracking-[0.15em] uppercase text-[#c8a96e] hover:text-[#1a1a1a] transition-colors duration-300"
                  style={{ fontFamily: "'Inter', sans-serif" }}>

                  Learn More About Us
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ====== EXPLORE BY CATEGORY CAROUSEL ====== */}
      <section className="bg-[#111]">
        <FadeInSection>
          <p
            className="text-center text-[11px] tracking-[0.4em] uppercase text-[#999] pt-16 pb-6"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Explore by Category
          </p>
        </FadeInSection>
        <CategoryCarousel />
        <div className="pb-10" />
      </section>

      {/* ====== CUSTOMIZATION (full-width parallax) ====== */}
      <CustomizationParallax />

      {/* ====== ETHICAL PRACTICES ====== */}
      <section className="!pt-[100px] !pb-[96px] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInSection>
              <div>
                <h3 className="uppercase !text-[12px] !tracking-[0.5em] mb-4 text-[#c8a96e]"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                  {ethicalSection.label}
                </h3>
                <h2
                  className="!font-light !text-6xl text-[#1a1a1a]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>

                  {ethicalSection.heading}
                </h2>
                <p className="mt-6 text-[#555] text-[15px] leading-[1.8] max-w-lg"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                  {ethicalSection.paragraph}
                </p>
                <div className="mt-8 grid grid-cols-2 gap-6">
                  {ethicalSection.points.map((item) =>
                  <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#c8a96e]" />
                      <span className="text-[#555] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {item}
                      </span>
                    </div>
                  )}
                </div>
                <Link
                  to="/ethical-standards"
                  className="inline-flex items-center gap-2 mt-8 text-[12px] tracking-[0.15em] uppercase text-[#c8a96e] hover:text-[#1a1a1a] transition-colors duration-300"
                  style={{ fontFamily: "'Inter', sans-serif" }}>

                  Read Our Standards
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.2}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1772442125263-c9dd28bbd938?w=700&h=500&fit=crop"
                  alt="Ethical Practices"
                  className="w-full h-[450px] object-cover !mr-[80px]"
                  loading="lazy" />

              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ====== CONTACT CTA ====== */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <FadeInSection>
            <h2
              className="text-3xl md:text-4xl text-white font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>

              Let's Start a Conversation
            </h2>
            <p className="mt-4 text-white/50 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
              Reach out to us directly
            </p>
            <div className="mt-8 flex justify-center items-center gap-8 flex-wrap">
              <a
                href={`tel:${siteInfo.phone}`}
                className="flex items-center gap-3 px-6 py-3 border border-white/20 text-white/70 hover:border-[#c8a96e] hover:text-[#c8a96e] transition-all duration-300">

                <Phone className="w-4 h-4" />
                <span className="text-[13px] tracking-[0.1em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {siteInfo.phone}
                </span>
              </a>
              <a
                href={`mailto:${siteInfo.email}`}
                className="flex items-center gap-3 px-6 py-3 border border-white/20 text-white/70 hover:border-[#c8a96e] hover:text-[#c8a96e] transition-all duration-300">

                <Mail className="w-4 h-4" />
                <span className="text-[13px] tracking-[0.1em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {siteInfo.email}
                </span>
              </a>
            </div>
            <div className="mt-10 flex justify-center items-center gap-3">
              <img
                src={jbtBadge.logo}
                alt={jbtBadge.name}
                className="h-8 w-auto object-contain opacity-60" />

              <span className="text-white/30 text-[10px] tracking-[0.12em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}>
                {jbtBadge.name} Member
              </span>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>);

};

export default HomePage;